import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import DMCTabs from "../DMCS/DMCTabs";
import { format } from "date-fns";
import Modal from "react-bootstrap/Modal";
import currencyFlags from "../../Data/CurrencyFlags.json";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import { toast } from "react-toastify";

const CompanyDMCS = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [dmcs, setDmcs] = useState([]);
  const [filteredDmcs, setFilteredDmcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Search and filter states - these only hold values, don't trigger API
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Filter states - these only hold values, don't trigger API
  const [planTypeFilter, setPlanTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Ref to store actual filter values for API calls
  const filtersRef = useRef({
    searchTerm: "",
    planTypeFilter: "",
    statusFilter: "",
    pageNumber: 1
  });

  // Edit/Add modal states
  const [editShow, setEditShow] = useState(false);
  const [editDMC, setEditDMC] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [editBtnLoading, setEditBtnLoading] = useState(false);

  // Validation states
  const [formErrors, setFormErrors] = useState({});

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [dmcToChangePassword, setDmcToChangePassword] = useState(null);
  const [passwordData, setPasswordData] = useState({ newPassword: "", confirmPassword: "" });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordBtnLoading, setPasswordBtnLoading] = useState(false);

  // Countries and currencies state
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // Process countries data from currencyFlags.json
  useEffect(() => {
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );
      setCountries(sortedCountries);

      // Extract unique currencies
      const uniqueCurrencies = [];
      const currencyMap = new Map();

      sortedCountries.forEach((country) => {
        if (country.code && !currencyMap.has(country.code)) {
          currencyMap.set(country.code, true);
          uniqueCurrencies.push({
            code: country.code,
            name: country.currency || "",
          });
        }
      });

      // Sort currencies alphabetically by code
      uniqueCurrencies.sort((a, b) => a.code.localeCompare(b.code));
      setCurrencies(uniqueCurrencies);
    }
  }, []);

  // Get DMC data - uses refs for current values
  const GetData = useCallback(async (currentPage = 1) => {
    if (!user) return;
    setLoading(true);
    try {
      // Get values from refs
      const currentSearch = filtersRef.current.searchTerm;
      const currentPlanType = filtersRef.current.planTypeFilter;
      const currentStatus = filtersRef.current.statusFilter;
      const currentPageNumber = currentPage || filtersRef.current.pageNumber;

      // Build query parameters for DMC type
      let apiUrl = `${config.GETCOMPANIES}?companyType=DMC&PageNumber=${currentPageNumber}&PageSize=${pageSize}`;

      // Add search parameter if search term exists
      if (currentSearch && currentSearch.trim()) {
        apiUrl += `&search=${encodeURIComponent(currentSearch.trim())}`;
      }

      // Add plan type filter
      if (currentPlanType) {
        apiUrl += `&planType=${currentPlanType}`;
      }

      // Add status filter
      if (currentStatus !== "") {
        apiUrl += `&isActive=${currentStatus}`;
      }

      console.log("API URL:", apiUrl); // Debug log

      const response = await apiService.getWithParams(apiUrl);

      const dmcsData = response.data || [];
      setDmcs(dmcsData);
      setFilteredDmcs(dmcsData);
      setTotalPages(response.totalPages || 0);
      setTotalRecords(response.totalRecords || 0);
      setPageNumber(response.pageNumber || 1);
      setPageSize(response.pageSize || 20);
      
      // Update refs
      filtersRef.current.pageNumber = currentPageNumber;
    } catch (error) {
      setDmcs([]);
      setFilteredDmcs([]);
    } finally {
      setLoading(false);
    }
  }, [user, pageSize]);

  // Initial load
  useEffect(() => {
    if (!authLoading && user) {
      // Set default filters
      filtersRef.current.searchTerm = "";
      filtersRef.current.planTypeFilter = "";
      filtersRef.current.statusFilter = "";
      filtersRef.current.pageNumber = 1;
      GetData(1);
    }
  }, [user, authLoading, GetData]);

  // Handle plan type dropdown change - only updates state, doesn't call API
  const handlePlanTypeFilter = (e) => {
    const value = e.target.value;
    setPlanTypeFilter(value);
  };

  // Handle status dropdown change - only updates state, doesn't call API
  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
  };

  // Reset all filters - clears selections and calls API with empty filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setPlanTypeFilter("");
    setStatusFilter("");
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Reset refs
    filtersRef.current.searchTerm = "";
    filtersRef.current.planTypeFilter = "";
    filtersRef.current.statusFilter = "";
    filtersRef.current.pageNumber = 1;
    
    setPageNumber(1);
    GetData(1);
  };

  // Handle search button click - triggers API call with all filters
  const handleSearchButtonClick = () => {
    // Update refs with current values
    filtersRef.current.searchTerm = searchTerm;
    filtersRef.current.planTypeFilter = planTypeFilter;
    filtersRef.current.statusFilter = statusFilter;
    filtersRef.current.pageNumber = 1;
    
    setPageNumber(1);
    GetData(1);
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchButtonClick();
    }
  };

  // Generate search suggestions
  const generateSuggestions = (searchValue) => {
    if (!searchValue.trim()) {
      setSuggestions([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();
    const companyMatches = dmcs
      .filter((dmc) => dmc.companyName?.toLowerCase().includes(searchLower))
      .slice(0, 5);

    const brandMatches = dmcs
      .filter(
        (dmc) =>
          dmc.brandName?.toLowerCase().includes(searchLower) &&
          !dmc.companyName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 3);

    const emailMatches = dmcs
      .filter(
        (dmc) =>
          dmc.email?.toLowerCase().includes(searchLower) &&
          !dmc.companyName?.toLowerCase().includes(searchLower) &&
          !dmc.brandName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 2);

    const allSuggestions = [
      ...companyMatches,
      ...brandMatches,
      ...emailMatches,
    ];
    setSuggestions(allSuggestions);
  };

  // Handle search input change - only updates input, doesn't call API
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(true);
    
    // Generate suggestions for dropdown
    generateSuggestions(value);
  };

  // Handle suggestion click
  const handleSuggestionClick = (dmc) => {
    setSearchTerm(dmc.companyName);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Update refs and call API
    filtersRef.current.searchTerm = dmc.companyName;
    filtersRef.current.planTypeFilter = planTypeFilter;
    filtersRef.current.statusFilter = statusFilter;
    filtersRef.current.pageNumber = 1;
    
    setPageNumber(1);
    GetData(1);
  };

  // Handle input blur (hide suggestions after a delay)
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    // Don't automatically search, wait for user to click search button
  };

  const handleOpenPasswordModal = (dmc) => {
    setDmcToChangePassword(dmc);
    setShowPasswordModal(true);
  };

  // Close the password change modal and reset states
  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setDmcToChangePassword(null);
    setPasswordData({ newPassword: "", confirmPassword: "" });
    setPasswordErrors({});
    setPasswordBtnLoading(false);
  };

  // Handle input change in the password modal
  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
    // Clear error as user types
    if (passwordErrors[name]) {
      setPasswordErrors({ ...passwordErrors, [name]: null });
    }
  };

  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordData.newPassword || passwordData.newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters long.";
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle the submission of the new password
  const handleChangePassword = async () => {
    if (validatePasswordForm()) {
      setPasswordBtnLoading(true);
      try {
        const payload = {
          UserId: dmcToChangePassword.userId,
          NewPassword: passwordData.newPassword,
        };

        const res = await apiService.post(config.CHANGEPASSWORD, payload);

        if (res === true) {
          toast.success(`Password for ${dmcToChangePassword.companyName} changed successfully!`);
          handleClosePasswordModal();
        } else {
          toast.error("Failed to change password. The user might not exist or there was a server error.");
        }
      } catch (error) {
        toast.error("An error occurred. Please try again.");
      } finally {
        setPasswordBtnLoading(false);
      }
    }
  };

  // Pagination functions
  const GetFirstPageData = () => {
    GetData(1);
  };

  const GetLastPageData = () => {
    GetData(totalPages);
  };

  const GetPageData = (number) => {
    setPageNumber(number);
    filtersRef.current.pageNumber = number;
    if (pageNumber !== number) GetData(number);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = pageNumbers.map((number) => {
    return (
      <li
        className={`page-item ${pageNumber === number ? "active" : ""}`}
        key={number}
        onClick={() => GetPageData(number)}
      >
        <Link className="page-link">{number}</Link>
      </li>
    );
  });

  // Handle Add DMC button click
  const handleAddDMC = () => {
    const newDMC = {
      companyId: "",
      companyName: "",
      brandName: "",
      email: "",
      phoneNumber: "",
      gstNo: "",
      panCard: "",
      address: "",
      city: "",
      state: "",
      country: "IN",
      currency: "INR",
      primaryDomain: "",
      isActive: true,
      planType: "TRIAL",
      companyType: "DMC",
      connects: 0,
      isWebsite: false,
      isRenewal: false,
      endDate: new Date(),
      whatsAppNumber: "",
      whatsAppPhoneNumberId: "",
      whatsAppPermanentToken: "",
      isChatBot: false,
      isSupplierAPI: false,
    };

    setEditDMC(newDMC);
    setLogoPreview(null);
    setLogoFile(null);
    setFormErrors({});
    setEditShow(true);
  };

  // Handle edit icon click
  const handleEditClick = (dmc) => {
    const cleanedData = {};
    Object.keys(dmc).forEach((key) => {
      if (
        dmc[key] === null ||
        dmc[key] === "null" ||
        dmc[key] === "undefined"
      ) {
        cleanedData[key] = "";
      } else {
        cleanedData[key] = dmc[key];
      }
    });

    setEditDMC(cleanedData);
    setLogoPreview(cleanedData.logoUrl || cleanedData.logoSrc);
    setFormErrors({});
    setEditShow(true);
  };

  // Close edit modal
  const handleEditClose = () => {
    setEditShow(false);
    setEditDMC({});
    setLogoFile(null);
    setLogoPreview(null);
    setFormErrors({});
  };

  // Handle file change for logo
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  // Handle country change
  const handleCountryChange = (countryCode) => {
    const selectedCountry = countries.find(
      (country) => country.countryCode === countryCode
    );
    setEditDMC({
      ...editDMC,
      country: countryCode,
      currency: selectedCountry?.code || "",
    });
  };

  // Handle currency change
  const handleCurrencyChange = (currencyCode) => {
    setEditDMC({
      ...editDMC,
      currency: currencyCode,
    });
  };

  // Check if country is India
  const isIndia = (countryCode) => {
    return countryCode === "IN";
  };

  // Handle input change for edit form
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldsToUppercase = [
      "companyName",
      "brandName",
      "city",
      "state",
      "gstNo",
      "panCard",
    ];

    setEditDMC({
      ...editDMC,
      [name]:
        type === "checkbox"
          ? checked
          : fieldsToUppercase.includes(name)
          ? value.toUpperCase()
          : value,
    });

    // Clear the specific error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null,
      });
    }
  };

  // VALIDATION LOGIC
  const validateDMCForm = () => {
    const newErrors = {};

    // Company Name validation
    if (!editDMC.companyName || editDMC.companyName.trim() === "") {
      newErrors.companyName = "Company Name is required.";
    }

    // Brand Name validation
    if (!editDMC.brandName || editDMC.brandName.trim() === "") {
      newErrors.brandName = "Brand Name is required.";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!editDMC.email || !emailRegex.test(editDMC.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Phone Number validation
    const phoneRegex = /^\d{10,15}$/;
    if (!editDMC.phoneNumber || !phoneRegex.test(editDMC.phoneNumber)) {
      newErrors.phoneNumber =
        "Please enter a valid phone number (10-15 digits).";
    }

    setFormErrors(newErrors);
    // Return true if there are no errors
    return Object.keys(newErrors).length === 0;
  };

  // Handle update DMC
  const handleUpdateDMC = async (e) => {
    e.preventDefault();
    if (validateDMCForm()) {
      setEditBtnLoading(true);

      try {
        if (!editDMC.companyId) {
          const newDMCData = {
            companyName: editDMC.companyName || "",
            brandName: editDMC.brandName || "",
            email: editDMC.email || "",
            phoneNumber: editDMC.phoneNumber || "",
            gstNo: editDMC.gstNo || "",
            panCard: editDMC.panCard || "",
            address: editDMC.address || "",
            city: editDMC.city || "",
            state: editDMC.state || "",
            country: editDMC.country || "",
            currency: editDMC.currency || "",
            companyType: "DMC",
            parentCompanyId: user?.cid,
            isActive: editDMC.isActive,
            planType: editDMC.planType || "TRIAL",
            primaryDomain: editDMC.primaryDomain || "",
            connects: editDMC.connects || 0,
            isWebsite: editDMC.isWebsite || false,
            isRenewal: editDMC.isRenewal || false,
            endDate: editDMC.endDate || new Date(),
            whatsAppNumber: editDMC.whatsAppNumber || "",
            whatsAppPhoneNumberId: editDMC.whatsAppPhoneNumberId || "",
            whatsAppPermanentToken: editDMC.whatsAppPermanentToken || "",
            isChatBot: editDMC.isChatBot || false,
            isSupplierAPI: editDMC.isSupplierAPI || false,
            commissionRate: editDMC.commissionRate || 10,
            userType: "DMCUSER",
            password: "App@000",
          };

          const response = await apiService.create(
            config.CREATECOMPANYBYSUPERADMIN,
            newDMCData
          );

          if (response && response.success) {
            await GetData(pageNumber);
            handleEditClose();
          } else {
            toast.error("Failed to create DMC. Please try again.");
          }
        } else {
          const formData = new FormData();

          formData.append("companyId", editDMC.companyId);
          formData.append("companyName", editDMC.companyName || "");
          formData.append("brandName", editDMC.brandName || "");
          formData.append("email", editDMC.email || "");
          formData.append("phoneNumber", editDMC.phoneNumber || "");
          formData.append("gstNo", editDMC.gstNo || "");
          formData.append("panCard", editDMC.panCard || "");
          formData.append("address", editDMC.address || "");
          formData.append("city", editDMC.city || "");
          formData.append("state", editDMC.state || "");
          formData.append("country", editDMC.country || "");
          formData.append("currency", editDMC.currency || "");
          formData.append("primaryDomain", editDMC.primaryDomain || "");
          formData.append("isActive", editDMC.isActive);
          formData.append("planType", editDMC.planType || "");
          formData.append("connects", editDMC.connects || 0);
          formData.append("isWebsite", editDMC.isWebsite || false);
          formData.append("isRenewal", editDMC.isRenewal || false);
          formData.append("endDate", editDMC.endDate || "");
          formData.append("whatsAppNumber", editDMC.whatsAppNumber || "");
          formData.append(
            "whatsAppPhoneNumberId",
            editDMC.whatsAppPhoneNumberId || ""
          );
          formData.append(
            "whatsAppPermanentToken",
            editDMC.whatsAppPermanentToken || ""
          );
          formData.append("isChatBot", editDMC.isChatBot || false);
          formData.append("isSupplierAPI", editDMC.isSupplierAPI || false);
          formData.append("commissionRate", editDMC.commissionRate || 10);

          if (logoFile) {
            formData.append("logoFile", logoFile);
          }

          await apiService.update(config.UPDATECOMPANYBYSUPERADMIN, formData);
          await GetData(pageNumber);
          handleEditClose();
        }
      } catch (error) {
        toast.error("An error occurred. Please try again.");
      } finally {
        setEditBtnLoading(false);
      }
    }
  };

  // Delete functionality
  const handleOpenDeleteModal = (dmcId) => {
    setItemToDelete(dmcId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        await apiService.remove(config.DELETECOMPANY, itemToDelete);
        await GetData(pageNumber);
        toast.success("DMC deleted successfully!");
      } catch (error) {
        toast.error("Failed to delete DMC.");
      }
      handleCloseDeleteModal();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "dd MMM, yyyy hh:mm a");
  };

  if (authLoading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <>
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <DMCTabs pageName="DMCS" />
              </div>

              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="card-title mb-0">DMCS LIST</h5>
                      <button
                        className="btn btn-primary"
                        onClick={handleAddDMC}
                      >
                        <i className="ri-add-line me-2"></i>
                        Add DMC
                      </button>
                    </div>

                    {/* Filter Section */}
                    <div
                      className="row align-items-end mb-4"
                      style={{ gap: "8px 0" }}
                    >
                      {/* Search Bar with Button */}
                      <div className="col-md-5">
                        <div style={{ position: "relative" }}>
                          <div className="input-group" style={{ height: "45px" }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search by company name, brand name, or email..."
                              value={searchTerm}
                              onChange={handleSearch}
                              onKeyPress={handleSearchKeyPress}
                              onFocus={() => setShowSuggestions(true)}
                              onBlur={handleInputBlur}
                              style={{
                                height: "45px",
                                paddingLeft: "40px",
                                position: "relative",
                              }}
                            />
                            <i
                              className="ri-search-line"
                              style={{
                                position: "absolute",
                                left: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 10,
                                color: "#6c757d",
                              }}
                            ></i>
                            {searchTerm && (
                              <button
                                className="btn position-absolute end-0 top-0 bottom-0"
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  zIndex: 10,
                                }}
                                onClick={clearSearch}
                              >
                                <i className="ri-close-line fs-5"></i>
                              </button>
                            )}
                          </div>

                          {/* Suggestions dropdown */}
                          {showSuggestions && suggestions.length > 0 && (
                            <div
                              style={{
                                position: "absolute",
                                width: "100%",
                                marginTop: "4px",
                                border: "1px solid #dee2e6",
                                backgroundColor: "white",
                                borderRadius: "6px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                zIndex: 1000,
                              }}
                            >
                              {suggestions.map((dmc) => (
                                <div
                                  key={dmc.companyId}
                                  style={{
                                    padding: "8px 12px",
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    backgroundColor: "white",
                                    transition: "background-color 0.2s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor =
                                      "#f8f9fa";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = "white";
                                  }}
                                  onClick={() => handleSuggestionClick(dmc)}
                                >
                                  <i className="ri-user-shared-line me-2 text-secondary"></i>
                                  <div>
                                    <div style={{ fontWeight: "600" }}>
                                      {dmc.companyName}
                                    </div>
                                    {dmc.brandName &&
                                      dmc.brandName !==
                                        dmc.companyName && (
                                        <div
                                          style={{
                                            fontSize: "0.875rem",
                                            color: "#6c757d",
                                          }}
                                        >
                                          {dmc.brandName}
                                        </div>
                                      )}
                                    {dmc.email && (
                                      <div
                                        style={{
                                          fontSize: "0.875rem",
                                          color: "#6c757d",
                                        }}
                                      >
                                        {dmc.email}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Plan Type Filter */}
                      <div className="col-md-2">
                        <label className="form-label">Plan Type</label>
                        <select
                          className="form-select"
                          value={planTypeFilter}
                          onChange={handlePlanTypeFilter}
                          style={{ height: "45px" }}
                        >
                          <option value="">All Plans</option>
                          <option value="TRIAL">TRIAL</option>
                          <option value="MONTHLYSTARTER">MONTHLYSTARTER</option>
                          <option value="MONTHLYPRO">MONTHLYPRO</option>
                          <option value="MONTHLYENTERPRISE">
                            MONTHLYENTERPRISE
                          </option>
                          <option value="CUSTOM">CUSTOM</option>
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div className="col-md-2">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={statusFilter}
                          onChange={handleStatusFilter}
                          style={{ height: "45px" }}
                        >
                          <option value="">All Status</option>
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      </div>

                      {/* Search and Reset Buttons - side by side */}
                      <div className="col-md-2">
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-primary flex-grow-1"
                            onClick={handleSearchButtonClick}
                            style={{ height: "45px" }}
                          >
                            <i className="ri-search-line me-1"></i>
                            Search
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={handleResetFilters}
                            style={{ height: "45px", minWidth: "45px" }}
                            title="Reset Filters"
                          >
                            <i className="ri-refresh-line"></i>
                               Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table Section with proper gap */}
                <div className="mt-4">
                  <div className="table-responsive table-card">
                    <table className="table align-middle table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>Company / Brand</th>
                          <th>Contact</th>
                          <th>Plan</th>
                          <th>Connects Left</th>
                          <th>Expires On</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <p className="mt-2">Loading DMCs...</p>
                            </td>
                          </tr>
                        ) : filteredDmcs.length > 0 ? (
                          filteredDmcs.map((dmc, index) => (
                            <tr key={dmc.companyId}>
                              <td>{index + 1}</td>
                              <td>
                                <div>
                                  <strong>{dmc.companyName}</strong>
                                </div>
                                <small className="text-muted">
                                  {dmc.brandName || "-"}
                                </small>
                              </td>
                              <td>
                                <div>{dmc.email}</div>
                                <small className="text-muted">
                                  {dmc.phoneNumber}
                                </small>
                              </td>
                              <td>
                                <span className="badge bg-primary">
                                  {dmc.planType || "N/A"}
                                </span>
                              </td>
                              <td>{dmc.connects}</td>
                              <td>{formatDate(dmc.endDate)}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    dmc.isActive ? "bg-success" : "bg-danger"
                                  }`}
                                >
                                  {dmc.isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td>
                                <ul className="list-inline hstack gap-2 mb-0">
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="Edit"
                                  >
                                    <Link
                                      className="edit-item-btn"
                                      onClick={(e) => handleEditClick(dmc)}
                                    >
                                      <i className="ri-pencil-fill align-bottom text-muted" />
                                    </Link>
                                  </li>
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="Change Password"
                                  >
                                    <Link
                                      className="edit-item-btn"
                                      onClick={(e) => handleOpenPasswordModal(dmc)}
                                    >
                                      <i className="ri-lock-password-fill align-bottom text-muted" />
                                    </Link>
                                  </li>
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="Delete"
                                  >
                                    <Link
                                      className="remove-item-btn"
                                      onClick={(e) =>
                                        handleOpenDeleteModal(dmc.companyId)
                                      }
                                    >
                                      <i className="ri-delete-bin-fill align-bottom text-muted" />
                                    </Link>
                                  </li>
                                </ul>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              No DMCs found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination section */}
                {filteredDmcs.length > 0 && (
                  <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                      <div className="text-muted">
                        Showing{" "}
                        <span className="fw-semibold">
                          {filteredDmcs.length}
                        </span>{" "}
                        of <span className="fw-semibold">{totalRecords}</span>{" "}
                        Results
                      </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                      <li className="page-item" onClick={GetFirstPageData}>
                        <Link className="page-link">Previous</Link>
                      </li>
                      {renderPageNumbers}
                      <li className="page-item" onClick={GetLastPageData}>
                        <Link className="page-link">Next</Link>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit/Add DMC Modal */}
        <Modal
          show={editShow}
          onHide={handleEditClose}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editDMC.companyId
                ? `Edit DMC - ${editDMC.companyName}`
                : "Add New DMC"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <div className="container-fluid">
              {/* Company Logo */}
              <div className="row mb-4">
                <label className="col-sm-3 col-form-label">Company Logo:</label>
                <div className="col-sm-9">
                  <div className="d-flex align-items-center gap-3">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Company Logo"
                        className="img-thumbnail"
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        className="img-thumbnail d-flex align-items-center justify-content-center"
                        style={{
                          width: "120px",
                          height: "120px",
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #dee2e6",
                        }}
                      >
                        <i className="ri-user-shared-line text-muted fs-2"></i>
                      </div>
                    )}
                    {editShow && (
                      <div className="flex-grow-1">
                        <input
                          type="file"
                          className="form-control"
                          accept="image/png, image/jpeg, image/gif"
                          onChange={handleFileChange}
                        />
                        <div className="form-text">
                          Recommended size: 120x120px. Supports PNG, JPG, GIF.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Company Name */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Company Name:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${
                      formErrors.companyName ? "is-invalid" : ""
                    }`}
                    value={editDMC.companyName || ""}
                    maxLength={40}
                    name="companyName"
                    onChange={handleEditInputChange}
                    placeholder="Enter company name"
                  />
                  {formErrors.companyName && (
                    <div className="invalid-feedback">
                      {formErrors.companyName}
                    </div>
                  )}
                </div>
              </div>

              {/* Brand Name */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Brand Name:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${
                      formErrors.brandName ? "is-invalid" : ""
                    }`}
                    value={editDMC.brandName || ""}
                    maxLength={40}
                    name="brandName"
                    onChange={handleEditInputChange}
                    placeholder="Enter brand name"
                  />
                  {formErrors.brandName && (
                    <div className="invalid-feedback">
                      {formErrors.brandName}
                    </div>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Email:</label>
                <div className="col-sm-9">
                  <input
                    type="email"
                    className={`form-control ${
                      formErrors.email ? "is-invalid" : ""
                    }`}
                    value={editDMC.email || ""}
                    maxLength={40}
                    name="email"
                    onChange={handleEditInputChange}
                    placeholder="Enter email address"
                  />
                  {formErrors.email && (
                    <div className="invalid-feedback">{formErrors.email}</div>
                  )}
                </div>
              </div>

              {/* Phone */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Phone:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${
                      formErrors.phoneNumber ? "is-invalid" : ""
                    }`}
                    value={editDMC.phoneNumber || ""}
                    maxLength={15}
                    name="phoneNumber"
                    onChange={handleEditInputChange}
                    placeholder="Enter phone number"
                  />
                  {formErrors.phoneNumber && (
                    <div className="invalid-feedback">
                      {formErrors.phoneNumber}
                    </div>
                  )}
                </div>
              </div>
              
              {/* City */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">City:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editDMC.city || ""}
                    maxLength={40}
                    name="city"
                    onChange={handleEditInputChange}
                    placeholder="Enter city"
                  />
                </div>
              </div>

              {/* State */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">State:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editDMC.state || ""}
                    maxLength={40}
                    name="state"
                    onChange={handleEditInputChange}
                    placeholder="Enter state"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Address:</label>
                <div className="col-sm-9">
                  <textarea
                    className="form-control"
                    value={editDMC.address || ""}
                    maxLength={250}
                    name="address"
                    onChange={handleEditInputChange}
                    placeholder="Enter address"
                    rows="3"
                  />
                </div>
              </div>

              {/* Country */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Country:</label>
                <div className="col-sm-9">
                  <select
                    className="form-select"
                    value={editDMC.country || ""}
                    name="country"
                    onChange={(e) => handleCountryChange(e.target.value)}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option
                        key={country.countryCode}
                        value={country.countryCode}
                      >
                        {country.flag && (
                          <img
                            src={country.flag}
                            alt={country.country}
                            style={{
                              width: "20px",
                              height: "15px",
                              marginRight: "8px",
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                        {country.country} - {country.countryCode}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Currency */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Currency:</label>
                <div className="col-sm-9">
                  <select
                    className="form-select"
                    value={editDMC.currency || ""}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                  >
                    <option value="">Select Currency</option>
                    {countries.map((country) => (
                      <option key={country.countryCode} value={country.code}>
                        {country.flag && (
                          <img
                            src={country.flag}
                            alt={country.country}
                            style={{
                              width: "20px",
                              height: "15px",
                              marginRight: "8px",
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                        {country.country} - {country.code}
                        {country.currency && ` (${country.currency})`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* GST No / Tax No */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  {isIndia(editDMC.country) ? "GST No:" : "Tax No:"}
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editDMC.gstNo || ""}
                    maxLength={15}
                    name="gstNo"
                    onChange={handleEditInputChange}
                    placeholder={
                      isIndia(editDMC.country)
                        ? "Enter GST number"
                        : "Enter tax number"
                    }
                  />
                </div>
              </div>

              {/* Pan Card - Only show for India */}
              {isIndia(editDMC.country) && (
                <div className="row mb-3">
                  <label className="col-sm-3 col-form-label">Pan Card:</label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      className="form-control"
                      value={editDMC.panCard || ""}
                      maxLength={15}
                      name="panCard"
                      onChange={handleEditInputChange}
                      placeholder="Enter PAN card number"
                    />
                  </div>
                </div>
              )}

              {/* WhatsApp Number */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  WhatsApp Number:
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editDMC.whatsAppNumber || ""}
                    maxLength={15}
                    name="whatsAppNumber"
                    onChange={handleEditInputChange}
                    placeholder="Enter WhatsApp number"
                  />
                </div>
              </div>

              {/* Primary Domain */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  Primary Domain:
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editDMC.primaryDomain || ""}
                    maxLength={100}
                    name="primaryDomain"
                    onChange={handleEditInputChange}
                    placeholder="e.g., https://example.com"
                  />
                </div>
              </div>
              
              {/* Plan Type */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Plan Type:</label>
                <div className="col-sm-9">
                  <select
                    className="form-select"
                    value={editDMC.planType || "TRIAL"}
                    name="planType"
                    onChange={handleEditInputChange}
                  >
                    <option value="TRIAL">TRIAL</option>
                    <option value="MONTHLYSTARTER">MONTHLYSTARTER</option>
                    <option value="MONTHLYPRO">MONTHLYPRO</option>
                    <option value="MONTHLYENTERPRISE">MONTHLYENTERPRISE</option>
                    <option value="CUSTOM">CUSTOM</option>
                  </select>
                </div>
              </div>

              {/* Connects */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Connects:</label>
                <div className="col-sm-9">
                  <input
                    type="number"
                    className="form-control"
                    value={editDMC.connects || 0}
                    name="connects"
                    onChange={handleEditInputChange}
                    placeholder="Enter number of connects"
                  />
                </div>
              </div>

              {/* End Date */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">End Date:</label>
                <div className="col-sm-9">
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={
                      editDMC.endDate
                        ? new Date(editDMC.endDate).toISOString().slice(0, 16)
                        : ""
                    }
                    name="endDate"
                    onChange={handleEditInputChange}
                  />
                </div>
              </div>

              {/* Status */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Status:</label>
                <div className="col-sm-9">
                  <select
                    className="form-select"
                    value={editDMC.isActive}
                    name="isActive"
                    onChange={handleEditInputChange}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>
              </div>

              {/* Separated Features Section */}
              <div className="row mb-3">
                <div className="col-12">
                  <h6 className="border-bottom pb-2 mb-3">Features</h6>
                </div>
              </div>

              {/* Website Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Website:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editDMC.isWebsite || false}
                      name="isWebsite"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Website Feature
                    </label>
                  </div>
                </div>
              </div>

              {/* Chat Bot Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Chat Bot:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editDMC.isChatBot || false}
                      name="isChatBot"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Chat Bot Feature
                    </label>
                  </div>
                </div>
              </div>

              {/* Supplier API Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Supplier API:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editDMC.isSupplierAPI || false}
                      name="isSupplierAPI"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Supplier API Feature
                    </label>
                  </div>
                </div>
              </div>

              {/* Renewal Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Renewal:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editDMC.isRenewal || false}
                      name="isRenewal"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Auto Renewal
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-center w-100">
              {editBtnLoading ? (
                <button className="btn btn-success me-2 px-4" disabled>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {editDMC.companyId ? "Saving..." : "Creating..."}
                </button>
              ) : (
                <button
                  className="btn btn-success me-2 px-4"
                  onClick={handleUpdateDMC}
                >
                  <i className="fas fa-save me-2"></i>
                  {editDMC.companyId ? "Save Changes" : "Create DMC"}
                </button>
              )}
              <button className="btn btn-danger px-4" onClick={handleEditClose}>
                <i className="fas fa-times me-2"></i>Cancel
              </button>
            </div>
          </Modal.Footer>
        </Modal>
        
        {/* Password Change Modal */}
        <Modal show={showPasswordModal} onHide={handleClosePasswordModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Change Password</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>You are changing the password for DMC: <strong>{dmcToChangePassword?.companyName}</strong></p>
            
            <div className="mb-3">
              <label className="form-label">New Password</label>
              <input
                type="password"
                name="newPassword"
                className={`form-control ${passwordErrors.newPassword ? 'is-invalid' : ''}`}
                value={passwordData.newPassword}
                onChange={handlePasswordInputChange}
                placeholder="Enter new password"
              />
              {passwordErrors.newPassword && <div className="invalid-feedback">{passwordErrors.newPassword}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                name="confirmPassword"
                className={`form-control ${passwordErrors.confirmPassword ? 'is-invalid' : ''}`}
                value={passwordData.confirmPassword}
                onChange={handlePasswordInputChange}
                placeholder="Confirm new password"
              />
              {passwordErrors.confirmPassword && <div className="invalid-feedback">{passwordErrors.confirmPassword}</div>}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-light" onClick={handleClosePasswordModal}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleChangePassword}
              disabled={passwordBtnLoading}
            >
              {passwordBtnLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                "Save Password"
              )}
            </button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
        />
      </div>
    </>
  );
};

export default CompanyDMCS;